import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Card, Skeleton, Toast } from '../primitives';
import { supabase } from '../../lib/supabase';
import {
  fetchTypePipelines,
  stagesFor
} from '../../lib/workItemPipelines';
import {
  AttachWorkItemCover,
  type AttachWorkItemRow,
  type AttachWorkItemType
} from '../AttachWorkItemCover';
import type { Database } from '../../types/database';

type WorkItemType = Database['public']['Enums']['work_item_type'];

export interface ChildTasksPanelProps {
  epicId: string;
  orgId: string;
  onNavigate: (id: string) => void;
  /** When true, render the rows without the surrounding `<Card>` chrome —
   * the consumer (e.g. InspectorTabs) supplies its own surface + padding. */
  naked?: boolean;
}

interface ChildRow {
  id: string;
  name: string;
  type: WorkItemType;
  status: string;
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready' }
  | { kind: 'error'; message: string };

const TYPE_LABEL: Record<string, string> = {
  story: 'Story',
  bug: 'Bug',
  task: 'Task'
};

export function ChildTasksPanel({
  epicId,
  orgId,
  onNavigate,
  naked = false
}: ChildTasksPanelProps) {
  const [load, setLoad] = useState<LoadState>({ kind: 'loading' });
  const [rows, setRows] = useState<ChildRow[]>([]);
  const [doneStage, setDoneStage] = useState<string>('Done');
  const [refreshTick, setRefreshTick] = useState(0);
  const [coverOpen, setCoverOpen] = useState(false);
  const [candidates, setCandidates] = useState<AttachWorkItemRow[]>([]);
  const [toast, setToast] = useState<{ message: string; undo?: () => void } | null>(
    null
  );

  useEffect(() => {
    let cancelled = false;
    setLoad({ kind: 'loading' });
    void (async () => {
      const [pipeRes, childrenRes] = await Promise.all([
        fetchTypePipelines([null], orgId),
        supabase
          .from('work_items')
          .select('id, name, type, status, rank')
          .eq('org_id', orgId)
          .eq('parent_work_item_id', epicId)
          .in('type', ['story', 'bug', 'task'])
          .order('rank', { ascending: true })
      ]);
      if (cancelled) return;
      if (pipeRes.error) {
        setLoad({ kind: 'error', message: pipeRes.error });
        return;
      }
      if (childrenRes.error) {
        setLoad({ kind: 'error', message: childrenRes.error.message });
        return;
      }
      const stages = stagesFor(pipeRes.byType, null);
      setDoneStage(stages[stages.length - 1]);
      setRows(
        (childrenRes.data ?? []).map((r) => ({
          id: r.id as string,
          name: (r.name as string) ?? '',
          type: r.type as WorkItemType,
          status: (r.status as string) ?? ''
        }))
      );
      setLoad({ kind: 'ready' });
    })();
    return () => {
      cancelled = true;
    };
  }, [epicId, orgId, refreshTick]);

  const openCover = useCallback(async () => {
    // Eligible children = parent-less Story / Bug / Task in this org, excluding
    // this Epic. (An Epic cannot itself be a Story/Bug/Task ancestor, and
    // existing children are already filtered out by the parent_work_item_id
    // IS NULL predicate.)
    const { data, error } = await supabase
      .from('work_items')
      .select('id, name, type, status, updated_at')
      .eq('org_id', orgId)
      .is('parent_work_item_id', null)
      .in('type', ['story', 'bug', 'task'])
      .order('updated_at', { ascending: false });
    if (error) {
      setToast({ message: `Could not load candidates: ${error.message}` });
      return;
    }
    const items: AttachWorkItemRow[] = (data ?? [])
      .filter((r) => (r.id as string) !== epicId)
      .map((r) => ({
        id: r.id as string,
        title: (r.name as string) ?? '',
        type: r.type as AttachWorkItemType,
        status: (r.status as string) ?? '',
        assigneeName: null,
        updatedAt: (r.updated_at as string) ?? new Date().toISOString()
      }));
    setCandidates(items);
    setCoverOpen(true);
  }, [epicId, orgId]);

  const handleAttach = useCallback(
    async (ids: string[]) => {
      if (ids.length === 0) return;
      // Single bulk RPC: re-parents every child in one transaction, so a
      // mid-batch failure rolls the whole thing back (no partially-linked state).
      const { error } = await supabase.rpc('set_work_item_parent_bulk', {
        p_work_item_ids: ids,
        p_parent_id: epicId
      });
      if (error) {
        setToast({ message: `Could not link children: ${error.message}` });
        return;
      }
      setRefreshTick((t) => t + 1);
      setToast({
        message: `Linked ${ids.length} ${ids.length === 1 ? 'child' : 'children'}.`
      });
    },
    [epicId]
  );

  const handleUnlink = useCallback(
    async (childId: string) => {
      const child = rows.find((r) => r.id === childId);
      if (!child) return;
      if (
        !window.confirm(
          'Unlink this child? The work item still exists in /work-items with no parent.'
        )
      ) {
        return;
      }
      const { error } = await supabase.rpc('set_work_item_parent', {
        p_work_item_id: childId,
        p_parent_id: null as unknown as string
      });
      if (error) {
        setToast({ message: `Could not unlink: ${error.message}` });
        return;
      }
      setRefreshTick((t) => t + 1);
      setToast({
        message: `Unlinked "${child.name || 'child'}".`,
        undo: async () => {
          const undoRes = await supabase.rpc('set_work_item_parent', {
            p_work_item_id: childId,
            p_parent_id: epicId
          });
          if (undoRes.error) {
            setToast({ message: `Undo failed: ${undoRes.error.message}` });
            return;
          }
          setRefreshTick((t) => t + 1);
          setToast({ message: 'Re-linked.' });
        }
      });
    },
    [epicId, rows]
  );

  const total = rows.length;
  const done = rows.filter((r) => r.status === doneStage).length;

  // `wrap` lets the caller opt out of the Card chrome via `naked` — used
  // when the panel renders inside InspectorTabs, which already supplies a
  // padded body. Pattern mirrors SubtasksChecklist + AttachedDocumentsPanel.
  const wrap = (
    status: 'loading' | 'ready' | 'error',
    title: string,
    children: ReactNode,
    extraData?: Record<string, string | number>
  ) => {
    if (naked) {
      return (
        <div
          className="wid-child-tasks-naked"
          data-testid="work-item-detail-child-tasks"
          data-status={status}
          {...(extraData ?? {})}
        >
          {children}
        </div>
      );
    }
    return (
      <Card
        data-testid="work-item-detail-child-tasks"
        data-status={status}
        {...(extraData ?? {})}
      >
        <Card.Head title={title} />
        <Card.Body>{children}</Card.Body>
      </Card>
    );
  };

  if (load.kind === 'loading') {
    return wrap(
      'loading',
      'Child work items',
      <Skeleton width="100%" height={20} />
    );
  }

  if (load.kind === 'error') {
    return wrap(
      'error',
      'Child work items',
      <p className="wid-child-tasks-error" role="alert">
        {load.message}
      </p>
    );
  }

  const readyBody = (
    <>
        {total === 0 ? (
          <p
            className="wid-child-tasks-empty"
            data-testid="work-item-detail-child-tasks-empty"
          >
            No child work items yet.
          </p>
        ) : (
          <ul className="wid-child-list">
            {rows.map((child) => (
              <li
                key={child.id}
                className="wid-child-row"
                data-testid={`work-item-detail-child-tasks-row-${child.id}`}
                data-done={child.status === doneStage ? 'true' : 'false'}
                data-type={child.type}
              >
                <span
                  className="wid-child-row-kind"
                  data-testid={`work-item-detail-child-tasks-type-${child.id}`}
                >
                  {(TYPE_LABEL[child.type] ?? child.type).toUpperCase()}
                </span>
                <button
                  type="button"
                  className="wid-child-row-title"
                  onClick={() => onNavigate(child.id)}
                  data-testid={`work-item-detail-child-tasks-name-${child.id}`}
                >
                  {child.name}
                </button>
                <span
                  className="wid-child-row-status"
                  data-testid={`work-item-detail-child-tasks-status-${child.id}`}
                  data-status={child.status}
                >
                  {child.status}
                </span>
                <button
                  type="button"
                  className="wid-row-unlink"
                  onClick={() => void handleUnlink(child.id)}
                  aria-label={`Unlink ${child.name || 'child'} from this Epic`}
                  title="Unlink (does not delete the work item)"
                  data-testid={`work-item-detail-child-tasks-unlink-${child.id}`}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        )}

        <button
          type="button"
          className="wid-add-row"
          onClick={() => void openCover()}
          data-testid="work-item-detail-child-tasks-add"
        >
          + Add child
        </button>
    </>
  );

  return (
    <>
      {wrap(
        'ready',
        `Child work items (${done} of ${total} done)`,
        readyBody,
        { 'data-count': total, 'data-done': done }
      )}

      <AttachWorkItemCover
        open={coverOpen}
        onClose={() => setCoverOpen(false)}
        onAttach={handleAttach}
        subjectId={epicId}
        mode="attach-as-child"
        workItems={candidates}
      />

      {toast ? (
        <Toast onDismiss={() => setToast(null)}>
          {toast.message}
          {toast.undo ? (
            <>
              {' '}
              <button
                type="button"
                onClick={() => {
                  const fn = toast.undo;
                  setToast(null);
                  if (fn) void fn();
                }}
                style={{
                  background: 'transparent',
                  border: 0,
                  color: 'inherit',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  font: 'inherit'
                }}
              >
                Undo
              </button>
            </>
          ) : null}
        </Toast>
      ) : null}
    </>
  );
}
